/**
 * 設定 → ユーザー管理（040、docs/29 §5 → #156、docs/65 §5・Figma「設定の残り（10/9）」⑲⑳㉑で iOS 風に）。管理者だけ。docs/29 の決まり・DB・API は変えていない
 *   #287（2026-11-01 射場さん ★1 A、docs/100）：画面の言葉は「招待」をやめて「追加」＋「案内メール」、状態は 設定待ち／利用中／停止中、「席」は「利用人数」
 *   ・上の帯「‹ 設定｜＋ 追加」（PC の2列は「‹ 設定」を出さず、題の右に「＋ 追加」）。上限のときは押せない（薄墨。id inviteBtn）
 *   ・節「料金プラン」：プラン名の行、右に「利用人数／上限」・›（/account/plan へ。id seats）。上限なら注を朱で1行（seatsFullParts の事実の文。id limitNote）、
 *     設定待ち・停止中を含むときは注に「（設定待ち 1人を含みます）」。追加する使い方の文は見出しの「？」へ（#92 決め2）
 *   ・節「ユーザー」：ユーザーの行（UserRow）＝人のアイコン・氏名（自分は「（あなた）」）・2行目＝権限と最終ログイン／設定待ち（案内を送った日）／停止中・権限（行を薄く）・›。
 *     行を押すと修正のシート（SettingsUserEdit.tsx の UserEditSheet）。id userList・data-user は今のまま
 *   ・追加は「＋ 追加」→ シート（SettingsUserInvite.tsx の UserInviteSheet）。URL は今のまま＝/settings/users/new・/:id でこの画面の上にシートが開き、
 *     閉じると /settings/users へ replace（#155 の振込口座と同じ作り。app.tsx は3つの URL ともこの画面）
 *   ・除外した利用者は出ない（{project}・見積の担当者名はそのまま残る）。同じメールで追加すると同じ利用者として戻る（この説明は見出しの「？」。#92）
 */
import { REST_MSG, ROLE_LABEL, USER_MSG, type UsersPage as UsersPageData, type UserRow as User } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, type MouseEvent } from "react";
import { Link, useLocation, useMatch, useNavigate, useParams } from "react-router";
import { api, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { GroupList, GroupNavRow, GroupSection, GroupValueRow, UserRow } from "../components/GroupList.jsx";
import { PersonIcon } from "../components/PersonIcon.jsx";
import { SettingsHead, useRestPageClass } from "../components/SettingsHead.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { t } from "../labels.js";
import { UserEditSheet } from "./SettingsUserEdit.jsx";
import { UserInviteSheet } from "./SettingsUserInvite.jsx";
import { usePcOnly } from "../lib/pcOnly.js";

export const USERS_KEY = ["users"] as const;
const LIST = "/settings/users";

/** 「9/22」 */
export const mdOf = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};
/** 「10/9 21:40」 */
const mdhmOf = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/**
 * 上限の文（USER_MSG.seatsFull。API の 409 と同じ文）を、事実の文と「追加するには…」（使い方）に分ける。
 * 画面の注には事実だけ（#92 決め2）。使い方は近くの「？」へ（ユーザーの節の見出しの「？」）
 */
export function seatsFullParts(seats: UsersPageData["seats"]): { facts: string; how: string } {
  const all = USER_MSG.seatsFull(seats.planLabel, seats.limit, seats.used);
  const at = all.indexOf("追加するには");
  return at < 0 ? { facts: all, how: "" } : { facts: all.slice(0, at), how: `${all.slice(at)}。` };
}

/** 「（設定待ち 1人・停止中 1人を含みます）」。どちらも0なら空 */
export function seatsIncludes(seats: UsersPageData["seats"]): string {
  const parts = [seats.invited ? REST_MSG.seatsInvited.replace("{n}", String(seats.invited)) : "", seats.disabled ? REST_MSG.seatsDisabled.replace("{n}", String(seats.disabled)) : ""].filter(Boolean).join("・");
  return parts ? REST_MSG.seatsIncludes.replace("{parts}", parts) : "";
}

/** 上限の注（朱の1行）：事実の文（「スモールプランの利用人数は3人までです（現在 3人。設定待ち・停止中を含む）。」） */
export function limitText(seats: UsersPageData["seats"]): string {
  return seatsFullParts(seats).facts;
}

/** ユーザーの行の2行目（Figma ⑲ → #287：「設定待ち（11/1 案内送信）」「停止中・担当者」「担当者・最終ログイン 10/9 21:40」「担当者・ログイン履歴なし」） */
function subOf(u: User): string {
  if (u.status === "invited") return REST_MSG.invitedOn.replace("{md}", mdOf(u.invitedAt));
  if (u.status === "disabled") return REST_MSG.disabledRow.replace("{role}", ROLE_LABEL[u.role]);
  return `${ROLE_LABEL[u.role]}・${u.lastLoginAt ? REST_MSG.lastLogin.replace("{at}", mdhmOf(u.lastLoginAt)) : REST_MSG.neverLoggedIn}`;
}

/** 開いたシート（履歴の key ごとに中身を作り直す。閉じる動きのあいだも描く） */
type SheetFor = { key: string; id: string; row: User | undefined };

export function SettingsUsersPage() {
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ 設定（シートの開け閉めは replace なので変わらない）
  const back = useBackNav({ to: "/settings", label: "設定" });
  const me = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  // 料金プランは PC 専用（#185）：スマホでは席の行を見るだけの行に（› を出さない）
  const pcOnly = usePcOnly();
  const { id } = useParams();
  const isNew = !!useMatch(`${LIST}/new`);
  /** URL が開けと言っているシート（"new"＝追加、uuid＝修正） */
  const want = isNew ? "new" : (id ?? null);
  const { toast, node } = useToast();
  const cls = useRestPageClass("userspage");
  const admin = me.data?.user.role === "admin";
  const q = useQuery({ queryKey: USERS_KEY, queryFn: () => api<UsersPageData>("GET", "/users"), enabled: admin });
  const target = want && want !== "new" ? q.data?.users.find((u) => u.id === want) : undefined;
  const open = admin && !!q.data && (want === "new" || !!target);
  usePageName(open ? (want === "new" ? REST_MSG.inviteTitle : REST_MSG.editTitle) : REST_MSG.usersTitle);
  const last = useRef<SheetFor | null>(null);
  if (open && want) {
    if (last.current?.key !== loc.key) last.current = { key: loc.key, id: want, row: target };
    else if (target) last.current.row = target;
  }
  /** シートを閉じたところ（URL が一覧に変わるまで）。除外した利用者の URL のまま一覧が描き直されても「該当する利用者がいません」を出さない */
  const closing = useRef(false);
  const close = () => {
    closing.current = true;
    nav(LIST, { replace: true });
  };
  // 無い利用者の URL（除外した・別の会社の利用者）：知らせて一覧へ
  useEffect(() => {
    if (!want) closing.current = false;
    else if (q.data && !closing.current && want !== "new" && !target) {
      toast(USER_MSG.notFound);
      nav(LIST, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, want, target]);
  /** 追加・保存・除外・案内メール再送・追加取消：一覧を差し替えて、シートを閉じてトースト（文の {project} は会社の言葉に） */
  const onSaved = (p: UsersPageData & { message: string }) => {
    qc.setQueryData(USERS_KEY, { users: p.users, seats: p.seats });
    toast(t(p.message));
    close();
  };

  // 担当者が開いたときは、ルートの守り（app.tsx の ADMIN_ONLY_PAGES）が止めの画面「この操作には管理者の権限が必要です」を出す（#186、docs/75 f）
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  const { users, seats } = q.data;
  const sheet = last.current;
  const includes = seatsIncludes(seats);
  const inviteBtn = (
    <Link
      className="btn text cotop-btn"
      id="inviteBtn"
      to={`${LIST}/new`}
      replace
      aria-disabled={seats.full ? "true" : undefined}
      tabIndex={seats.full ? -1 : undefined}
      onClick={(e: MouseEvent) => {
        if (seats.full) e.preventDefault();
      }}
    >
      {REST_MSG.invite}
    </Link>
  );

  return (
    <div className={cls} data-testid="users">
      <SettingsHead list back={back} title={REST_MSG.usersTitle} end={inviteBtn} />
      <GroupSection
        title={REST_MSG.usersSectionPlan}
        note={
          seats.full ? (
            <span className="shu" id="limitNote" role="status">
              {limitText(seats)}
            </span>
          ) : includes ? (
            <span id="seatsIncludes">{includes}</span>
          ) : undefined
        }
        data-testid="users-sec-plan"
      >
        <GroupList plain>
          {pcOnly.hidden("/account/plan") ? (
            <GroupValueRow title={seats.planLabel} value={<span id="seats">{REST_MSG.seats.replace("{used}", String(seats.used)).replace("{limit}", String(seats.limit))}</span>} testId="seats-row" />
          ) : (
            <GroupNavRow title={seats.planLabel} value={REST_MSG.seats.replace("{used}", String(seats.used)).replace("{limit}", String(seats.limit))} to="/account/plan" buttonId="seats" testId="seats-row" />
          )}
        </GroupList>
      </GroupSection>
      <GroupSection
        title={REST_MSG.usersSectionUsers}
        help={<Help testId="help-users" text={`${t(REST_MSG.usersHelp)}${seats.full ? seatsFullParts(seats).how : ""}`} />}
        data-testid="users-sec-users"
      >
        <GroupList id="userList">
          {users.map((u) => (
            <UserRow
              key={u.id}
              icon={<PersonIcon familyName={u.familyName} givenName={u.givenName} />}
              title={
                <>
                  {u.name || REST_MSG.noName}
                  {u.isSelf ? <span className="you">{REST_MSG.you}</span> : null}
                </>
              }
              sub={subOf(u)}
              to={`${LIST}/${u.id}`}
              dim={u.status === "disabled"}
              data-user={u.id}
              data-email={u.email}
              data-status={u.status}
              testId="user-row"
            />
          ))}
        </GroupList>
      </GroupSection>
      {sheet ? (
        sheet.id === "new" ? (
          <UserInviteSheet key={sheet.key} open={open} seats={seats} onClose={close} onSaved={onSaved} />
        ) : sheet.row ? (
          <UserEditSheet key={sheet.key} open={open} target={sheet.row} users={users} onClose={close} onSaved={onSaved} />
        ) : null
      ) : null}
      {node}
    </div>
  );
}
